import { useCallback, useEffect, useState } from "react";
import { Camera, Pencil, Plus, Save, UserPlus, Users, X } from "lucide-react";
import { useToast } from "@/shared/providers/ToastProvider";
import { FormField, inputClass, selectClass } from "./FormField";
import {
  fetchAgentPhotoUrl,
  fetchCompanyAgents,
  saveAgentDependant,
  saveAgentPackage,
  uploadAgentPhoto,
  saveCompanyAgent,
  type AgentPayload,
  type CoveredAgent,
  type Dependant,
  type DependantPayload,
} from "../api/claimsApi";

/** allowPhoto : l'Admin peut ajouter la photo d'un agent après l'import de la liste. */
type Props = { companyId: number; refreshKey?: number; allowPhoto?: boolean };

/** Vignette de la photo de l'agent (chargée avec le jeton de session). */
function AgentPhoto({ companyId, agent, version }: { companyId: number; agent: CoveredAgent; version: number }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!agent.has_photo) return;
    let current: string | null = null;
    let cancelled = false;
    fetchAgentPhotoUrl(companyId, agent.id)
      .then((value) => {
        current = value;
        if (!cancelled) setUrl(value);
      })
      .catch(() => setUrl(null));
    return () => {
      cancelled = true;
      if (current) URL.revokeObjectURL(current);
    };
  }, [companyId, agent.id, agent.has_photo, version]);

  return url ? (
    <img src={url} alt={`Photo de ${agent.name}`} className="h-9 w-9 shrink-0 rounded-full object-cover" />
  ) : (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-card-muted text-xs font-semibold text-muted" aria-hidden="true">
      {agent.name.slice(0, 1).toUpperCase()}
    </span>
  );
}

const EMPTY_AGENT: AgentPayload = { matricule: "", name: "", birthdate: null, gender: null, phone: null, status: "active" };
const EMPTY_DEPENDANT: DependantPayload = { name: "", relationship: "child", birthdate: null, gender: null, status: "active" };

function age(birthdate: string | null): string {
  if (!birthdate) return "";
  const years = Math.floor((Date.now() - new Date(birthdate).getTime()) / (365.25 * 86400000));
  return Number.isFinite(years) && years >= 0 ? `${years} ans` : "";
}

/**
 * Agents couverts d'une entreprise et leurs ayants droit (conjoint, enfants, parents) :
 * consultation, ajout et modification à la main, en complément de l'import de la liste de couverture.
 */
export function AgentsRegistry({ companyId, refreshKey = 0, allowPhoto = false }: Props) {
  const { showToast } = useToast();
  const [agents, setAgents] = useState<CoveredAgent[]>([]);
  const [packages, setPackages] = useState<Array<{ id: number; name: string }>>([]);
  const [relationships, setRelationships] = useState<Array<{ value: Dependant["relationship"]; label: string }>>([]);
  const [search, setSearch] = useState("");
  const [agentForm, setAgentForm] = useState<{ id?: number; data: AgentPayload } | null>(null);
  const [dependantForm, setDependantForm] = useState<{ employeeId: number; id?: number; data: DependantPayload } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [photoVersion, setPhotoVersion] = useState(0);

  const handlePhoto = (agentId: number, file: File | undefined) => {
    if (!file) return;
    void run(() => uploadAgentPhoto(companyId, agentId, file), "Photo enregistrée.").then((ok) => ok && setPhotoVersion((n) => n + 1));
  };

  const load = useCallback(() => {
    fetchCompanyAgents(companyId, search)
      .then((data) => {
        setAgents(data.agents);
        setPackages(data.packages);
        setRelationships(data.relationships);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Chargement impossible."));
  }, [companyId, search]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load, refreshKey]);

  useEffect(() => {
    setAgentForm(null);
    setDependantForm(null);
  }, [companyId]);

  const run = async (action: () => Promise<unknown>, message: string) => {
    setSaving(true);
    setError(null);
    try {
      await action();
      showToast(message);
      load();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Enregistrement impossible.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const dependantsCount = agents.reduce((n, a) => n + a.dependants.length, 0);

  return (
    <section className="theme-card flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <Users size={18} className="text-primary" />
          Agents et ayants droit ({agents.length} agents, {dependantsCount} ayants droit)
        </h2>
        <div className="flex flex-wrap gap-2">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nom ou matricule…" className={`${inputClass} sm:max-w-[16rem]`} />
          <button
            type="button"
            onClick={() => setAgentForm({ data: { ...EMPTY_AGENT } })}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground"
          >
            <UserPlus size={15} />
            Ajouter un agent
          </button>
        </div>
      </div>
      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      {agentForm ? (
        <div className="rounded-lg border border-border bg-card-muted/40 p-3">
          <h3 className="mb-3 text-sm font-semibold text-foreground">{agentForm.id ? `Modifier ${agentForm.data.name}` : "Nouvel agent"}</h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
            <FormField label="Matricule" required>
              <input value={agentForm.data.matricule} onChange={(e) => setAgentForm({ ...agentForm, data: { ...agentForm.data, matricule: e.target.value } })} className={inputClass} />
            </FormField>
            <FormField label="Nom complet" required className="md:col-span-2">
              <input value={agentForm.data.name} onChange={(e) => setAgentForm({ ...agentForm, data: { ...agentForm.data, name: e.target.value } })} className={inputClass} />
            </FormField>
            <FormField label="Date de naissance">
              <input type="date" value={agentForm.data.birthdate ?? ""} onChange={(e) => setAgentForm({ ...agentForm, data: { ...agentForm.data, birthdate: e.target.value || null } })} className={inputClass} />
            </FormField>
            <FormField label="Sexe">
              <select value={agentForm.data.gender ?? ""} onChange={(e) => setAgentForm({ ...agentForm, data: { ...agentForm.data, gender: (e.target.value || null) as AgentPayload["gender"] } })} className={selectClass}>
                <option value="">—</option>
                <option value="M">M</option>
                <option value="F">F</option>
              </select>
            </FormField>
            {allowPhoto && agentForm.id ? (
              <FormField label="Photo" className="md:col-span-6">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  aria-label={`Photo de ${agentForm.data.name}`}
                  className="text-sm"
                  onChange={(e) => handlePhoto(agentForm.id as number, e.target.files?.[0])}
                />
              </FormField>
            ) : null}
            <FormField label="Statut">
              <select value={agentForm.data.status} onChange={(e) => setAgentForm({ ...agentForm, data: { ...agentForm.data, status: e.target.value as AgentPayload["status"] } })} className={selectClass}>
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
              </select>
            </FormField>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              disabled={saving || !agentForm.data.matricule.trim() || !agentForm.data.name.trim()}
              onClick={() =>
                void run(() => saveCompanyAgent(companyId, agentForm.data, agentForm.id), agentForm.id ? "Agent mis à jour." : "Agent ajouté.").then(
                  (ok) => ok && setAgentForm(null),
                )
              }
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
            >
              <Save size={15} />
              Enregistrer
            </button>
            <button type="button" onClick={() => setAgentForm(null)} className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm text-foreground hover:bg-accent/20 hover:border-accent">
              <X size={14} />
              Annuler
            </button>
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-card-muted text-left text-xs uppercase text-muted">
            <tr>
              <th className="px-3 py-2">Code SFC</th>
              <th className="px-3 py-2">Matricule</th>
              <th className="px-3 py-2">Agent</th>
              <th className="px-3 py-2">Package</th>
              <th className="px-3 py-2">Ayants droit</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {agents.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-muted">Aucun agent. Importez la liste de couverture ou ajoutez un agent.</td>
              </tr>
            ) : null}
            {agents.map((agent) => (
              <tr key={agent.id} className={`border-t border-border align-top ${agent.status === "active" ? "" : "opacity-60"}`}>
                <td className="px-3 py-2 font-mono text-xs font-semibold text-foreground">{agent.family_code ?? "—"}</td>
                <td className="px-3 py-2 font-mono text-xs text-primary">{agent.matricule}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    {allowPhoto ? <AgentPhoto companyId={companyId} agent={agent} version={photoVersion} /> : null}
                    <div>
                      <span className="block font-medium text-foreground">{agent.name}</span>
                      <span className="block text-xs text-muted">
                        {[agent.gender, age(agent.birthdate), agent.status === "active" ? null : "inactif"].filter(Boolean).join(" · ") || "—"}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2">
                  {packages.length > 1 ? (
                    <select
                      aria-label={`Package de ${agent.name}`}
                      value={agent.package_id ?? ""}
                      disabled={saving}
                      className={selectClass}
                      onChange={(e) =>
                        void run(() => saveAgentPackage(companyId, agent.id, e.target.value ? Number(e.target.value) : null), "Package de l'agent mis à jour.")
                      }
                    >
                      <option value="">À choisir</option>
                      {packages.map((pkg) => (
                        <option key={pkg.id} value={pkg.id}>{pkg.name}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-sm text-foreground">{agent.package_name ?? "—"}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <ul className="flex flex-col gap-1">
                    {agent.dependants.map((d) => (
                      <li key={d.id} className={`flex items-center gap-2 text-xs ${d.status === "active" ? "text-foreground" : "text-muted line-through"}`}>
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">{d.relationship_label}</span>
                        {d.name}
                        <span className="text-muted">{age(d.birthdate)}</span>
                        <button
                          type="button"
                          aria-label={`Modifier ${d.name}`}
                          className="rounded p-0.5 text-muted hover:text-primary"
                          onClick={() => setDependantForm({ employeeId: agent.id, id: d.id, data: { name: d.name, relationship: d.relationship, birthdate: d.birthdate, gender: d.gender, status: d.status } })}
                        >
                          <Pencil size={12} />
                        </button>
                      </li>
                    ))}
                    {agent.dependants.length === 0 ? <li className="text-xs text-muted">Aucun</li> : null}
                  </ul>
                  {dependantForm && dependantForm.employeeId === agent.id ? (
                    <div className="mt-2 grid grid-cols-1 gap-2 rounded-lg bg-card-muted/40 p-2 md:grid-cols-5">
                      <FormField label="Nom" required className="md:col-span-2">
                        <input value={dependantForm.data.name} onChange={(e) => setDependantForm({ ...dependantForm, data: { ...dependantForm.data, name: e.target.value } })} className={inputClass} />
                      </FormField>
                      <FormField label="Lien">
                        <select value={dependantForm.data.relationship} onChange={(e) => setDependantForm({ ...dependantForm, data: { ...dependantForm.data, relationship: e.target.value as Dependant["relationship"] } })} className={selectClass}>
                          {relationships.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </FormField>
                      <FormField label="Naissance">
                        <input type="date" value={dependantForm.data.birthdate ?? ""} onChange={(e) => setDependantForm({ ...dependantForm, data: { ...dependantForm.data, birthdate: e.target.value || null } })} className={inputClass} />
                      </FormField>
                      <FormField label="Sexe">
                        <select value={dependantForm.data.gender ?? ""} onChange={(e) => setDependantForm({ ...dependantForm, data: { ...dependantForm.data, gender: (e.target.value || null) as DependantPayload["gender"] } })} className={selectClass}>
                          <option value="">—</option>
                          <option value="M">M</option>
                          <option value="F">F</option>
                        </select>
                      </FormField>
                      <label className="flex items-center gap-2 text-xs text-foreground md:col-span-2">
                        <input type="checkbox" checked={dependantForm.data.status === "active"} onChange={(e) => setDependantForm({ ...dependantForm, data: { ...dependantForm.data, status: e.target.checked ? "active" : "inactive" } })} />
                        Couvert (actif)
                      </label>
                      <div className="flex gap-2 md:col-span-3 md:justify-end">
                        <button
                          type="button"
                          disabled={saving || !dependantForm.data.name.trim()}
                          onClick={() =>
                            void run(
                              () => saveAgentDependant(companyId, dependantForm.employeeId, dependantForm.data, dependantForm.id),
                              dependantForm.id ? "Ayant droit mis à jour." : "Ayant droit ajouté.",
                            ).then((ok) => ok && setDependantForm(null))
                          }
                          className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
                        >
                          <Save size={13} />
                          Enregistrer
                        </button>
                        <button type="button" onClick={() => setDependantForm(null)} className="rounded-lg border border-border px-3 py-1.5 text-xs text-foreground hover:bg-accent/20 hover:border-accent">
                          Annuler
                        </button>
                      </div>
                    </div>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex flex-col items-end gap-1">
                    <button
                      type="button"
                      onClick={() => setDependantForm({ employeeId: agent.id, data: { ...EMPTY_DEPENDANT } })}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-primary hover:bg-accent/20"
                    >
                      <Plus size={13} />
                      Ayant droit
                    </button>
                    <button
                      type="button"
                      onClick={() => setAgentForm({ id: agent.id, data: { matricule: agent.matricule, name: agent.name, birthdate: agent.birthdate, gender: agent.gender, phone: agent.phone, status: agent.status } })}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted hover:bg-accent/20 hover:text-primary"
                    >
                      <Pencil size={13} />
                      Agent
                    </button>
                    {allowPhoto ? (
                      <label className="inline-flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-xs text-muted hover:bg-accent/20 hover:text-primary">
                        <Camera size={13} />
                        Photo
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="sr-only"
                          aria-label={`Photo de ${agent.name}`}
                          onChange={(e) => handlePhoto(agent.id, e.target.files?.[0])}
                        />
                      </label>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
